import { isSafeUrl } from "@memorang/helpers/common";
import { isWeb } from "@memorang/helpers/platform";
import React, { type ReactNode, type RefObject } from "react";
import {
	Linking,
	type Text as NativeText,
	Text as RNText,
	StyleSheet,
	type TextStyle,
	View,
} from "react-native";
import type {
	ASTNode,
	RenderFunction,
	RenderRules,
} from "react-native-markdown-renderer";
import { Text } from "react-native-paper";
import type { AppTheme } from "../../useAppTheme";
import { ZoomImage } from "./ZoomImage";
import { HighlightedText } from "./components/HighlightedText";
import { InsertableText } from "./components/InsertableText";
import { getFontFamilyMapping } from "./font-family-mapping";
import MathView from "./mathview";
import {
	type CustomStyle,
	type Styles,
	type TextAlign,
	normalizeFontSize,
} from "./styles";
import { walkInheritedStyle } from "./walk-inherited-style";

/** Split text on math placeholders, insertable tokens, highlights, and blank tokens. */
const TEXT_SPLIT_REGEX =
	/(MATH_PLACEHOLDER_\d+_|\[#.*?\]|\{\{blank(?:_[^}]+)?\}\}|\{\{.*?\}\})/;

const BLANK_TOKEN_REGEX = /^\{\{blank(?:_[^}]+)?\}\}$/;

/**
 * Highlight with a context prefix. `(ctx-id)` is handed back to the onPress
 * handler so the host component can resolve which highlight was clicked.
 *   `{{(hint-42)see page 13}}` → contextId = "hint-42", content = "see page 13"
 */
const HIGHLIGHT_CONTEXT_REGEX = /^\{\{\(([^)]+)\)([\s\S]+)\}\}$/;

/** Matches `[#...]` insertable tokens (paren-wrapped `[#(id+N)label]`, `[#stem]`, etc.). */
const INSERTABLE_REGEX = /^\[#[^\]]+\]$/;
/** Pulls `N` out of `(id+N)` for use in `numeric-N-box-outline`. */
const INSERTABLE_POSITION_REGEX = /\+(\d+)\)/;

export type RenderRulesOptions = {
	fontSize: number;
	textAlign: TextAlign;
	theme: AppTheme;
	customStyles?: CustomStyle;
	useCustomLink?: boolean;
	onInsert?: (content: string) => void;
	onHighlightPress?: (contextId?: string) => void;
	highlightRef?: RefObject<NativeText | null>;
	appFont?: string;
	disableInsertableText?: boolean;
	paragraphMarginVertical?: number;
	mathMap: Map<string, { math: string; block: boolean }>;
	renderBlank?: (index: number) => ReactNode;
};

export const getRenderRules = ({
	fontSize,
	textAlign,
	theme,
	customStyles,
	useCustomLink,
	onInsert,
	onHighlightPress,
	highlightRef,
	appFont = "opensans",
	disableInsertableText,
	paragraphMarginVertical = 0,
	mathMap,
	renderBlank,
}: RenderRulesOptions): RenderRules => {
	const fontFamilyMapping = getFontFamilyMapping(appFont);
	const blankOrder: string[] = [];

	const renderTextPart = (
		part: string,
		key: string,
		highlightStyle: TextStyle,
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
	): ReactNode => {
		if (BLANK_TOKEN_REGEX.test(part) && renderBlank) {
			let blankIndex = blankOrder.indexOf(part);
			if (blankIndex === -1) {
				blankIndex = blankOrder.length;
				blankOrder.push(part);
			}
			return (
				<View
					key={key}
					style={{ marginHorizontal: 2, justifyContent: "center" }}
				>
					{renderBlank(blankIndex)}
				</View>
			);
		}

		if (
			part.startsWith("MATH_PLACEHOLDER_") &&
			part.endsWith("_") &&
			mathMap.has(part)
		) {
			const entry = mathMap.get(part);
			if (!entry) {
				return null;
			}
			return <MathView key={key} math={entry.math} inline={!entry.block} />;
		}

		if (INSERTABLE_REGEX.test(part) && onInsert) {
			const positionStr = part.match(INSERTABLE_POSITION_REGEX)?.[1] ?? "";
			const content = part.slice(2, -1);
			return (
				<InsertableText
					key={key}
					content={content}
					onInsert={onInsert}
					disabled={disableInsertableText}
					position={Number.parseInt(positionStr, 10)}
				/>
			);
		}

		const highlightWithContext = part.match(HIGHLIGHT_CONTEXT_REGEX);
		if (highlightWithContext) {
			const [, contextId, content] = highlightWithContext;
			return (
				<HighlightedText
					key={key}
					content={content}
					contextId={contextId}
					ref={highlightRef}
					onPress={onHighlightPress}
					inheritedStyle={highlightStyle}
				/>
			);
		}

		if (part.startsWith("{{") && part.endsWith("}}")) {
			return (
				<HighlightedText
					key={key}
					content={part.slice(2, -2)}
					ref={highlightRef}
					onPress={onHighlightPress}
					inheritedStyle={highlightStyle}
				/>
			);
		}

		return part;
	};

	const resolveFontFamily = (
		inheritedStyles: TextStyle,
	): { resolved: string; isItalic: boolean } => {
		const weight =
			inheritedStyles.fontWeight ??
			(customStyles?.text as TextStyle | undefined)?.fontWeight ??
			"400";
		const baseFontFamily =
			fontFamilyMapping[weight as keyof typeof fontFamilyMapping] ??
			`${appFont}-regular`;
		const isItalic = inheritedStyles.fontStyle === "italic";
		const resolved = isItalic
			? baseFontFamily.endsWith("-bold")
				? `${appFont}-bolditalic`
				: `${appFont}-regularitalic`
			: baseFontFamily;
		return { resolved, isItalic };
	};

	const textRule: RenderFunction = (node, _children, parent, rawStyles) => {
		const styles = rawStyles as Styles;

		const inheritedStyles = walkInheritedStyle(
			parent as ASTNode[],
			styles as Record<string, TextStyle | undefined>,
		);

		const parts = node.content.split(TEXT_SPLIT_REGEX);
		const { resolved: resolvedFontFamily, isItalic } =
			resolveFontFamily(inheritedStyles);
		const highlightStyle: TextStyle = {
			fontFamily: resolvedFontFamily,
			...(inheritedStyles.fontWeight
				? { fontWeight: inheritedStyles.fontWeight }
				: {}),
			...(isItalic ? { fontStyle: "italic" as const } : {}),
			...(inheritedStyles.textDecorationLine
				? { textDecorationLine: inheritedStyles.textDecorationLine }
				: {}),
		};

		const textStyle: TextStyle = {
			color: theme.colors.text,
			...(customStyles?.text as TextStyle | undefined),
			fontSize: inheritedStyles.fontSize ?? fontSize,
			textAlign,
			lineHeight:
				inheritedStyles.fontSize === fontSize ? fontSize * 1.5 : undefined,
			fontFamily: resolvedFontFamily,
		};

		if (renderBlank) {
			return (
				<React.Fragment key={node.key}>
					{parts.map((part, index) => {
						const key = `${node.key}-${index}`;
						const rendered = renderTextPart(part, key, highlightStyle);
						if (rendered === part) {
							return (
								<Text key={key} style={{ ...textStyle, lineHeight: 40 }}>
									{part}
								</Text>
							);
						}
						return rendered;
					})}
				</React.Fragment>
			);
		}

		return (
			<RNText key={node.key} style={textStyle}>
				{parts.map((part, index) =>
					renderTextPart(part, `${node.key}-${index}`, highlightStyle),
				)}
			</RNText>
		);
	};

	const paragraphRule: RenderFunction = (node, children, parent) => {
		const items = React.Children.toArray(children);
		const insideListItem =
			Array.isArray(parent) &&
			(parent as ASTNode[]).some((p) => p.type === "list_item");
		const marginVertical = insideListItem
			? 0
			: normalizeFontSize(paragraphMarginVertical, fontSize);

		if (renderBlank) {
			return (
				<View
					key={node.key}
					style={[
						{
							flexDirection: "row",
							flexWrap: "wrap",
							alignItems: "center",
							marginVertical,
						},
						customStyles?.paragraph,
					]}
				>
					{items.map((item, index) => (
						<React.Fragment key={`${node.key}-${index}`}>{item}</React.Fragment>
					))}
				</View>
			);
		}

		return (
			<React.Fragment key={node.key}>
				{items.map((item, index) => (
					<RNText
						key={`${node.key}-${index}`}
						style={{
							textAlign,
							fontSize,
							lineHeight: fontSize * 1.5,
							marginVertical,
							...(customStyles?.paragraph as TextStyle | undefined),
						}}
					>
						{item}
					</RNText>
				))}
			</React.Fragment>
		);
	};

	const bulletListRule: RenderFunction = (node, children, parent) => {
		const nested =
			Array.isArray(parent) &&
			(parent as ASTNode[]).some((p) => p.type === "list_item");
		return (
			<View
				key={node.key}
				style={{ marginBottom: nested ? 0 : fontSize * 0.5 }}
			>
				{children as ReactNode[]}
			</View>
		);
	};

	const strongRule: RenderFunction = (node, children) => (
		<Text
			key={node.key}
			style={{
				fontFamily: `${appFont}-bold`,
				fontSize,
				lineHeight: fontSize * 1.5,
			}}
		>
			{children as ReactNode[]}
		</Text>
	);

	const emRule: RenderFunction = (node, children, parent) => {
		const hasStrongAncestor =
			Array.isArray(parent) &&
			(parent as ASTNode[]).some((p) => p.type === "strong");
		const fontFamily = hasStrongAncestor
			? `${appFont}-bolditalic`
			: `${appFont}-regularitalic`;
		return (
			<Text
				key={node.key}
				style={{
					fontFamily,
					fontStyle: "italic",
					lineHeight: fontSize * 1.5,
				}}
			>
				{children as ReactNode[]}
			</Text>
		);
	};

	const insRule: RenderFunction = (node, children) => (
		<Text key={node.key} style={{ textDecorationLine: "underline", fontSize }}>
			{children as ReactNode[]}
		</Text>
	);

	const hardbreakRule: RenderFunction = (node) => (
		<Text key={node.key}>{"\n"}</Text>
	);

	const softbreakRule: RenderFunction = (node) => (
		<Text key={node.key}>{"\n"}</Text>
	);

	const listItemRule: RenderFunction = (node, children, parent, rawStyles) => {
		const styles = rawStyles as Styles;
		const parents = parent as ASTNode[];
		const inheritedStyles = walkInheritedStyle(
			parents,
			styles as Record<string, TextStyle | undefined>,
		);
		const refStyle = {
			...inheritedStyles,
			...StyleSheet.flatten(styles.list_item),
		};
		const textProps = [
			"fontSize",
			"lineHeight",
			"fontWeight",
			"fontFamily",
			"fontStyle",
			"color",
			"letterSpacing",
		] as const;
		const iconStyle: Record<string, unknown> = {};
		for (const prop of textProps) {
			if (refStyle[prop] !== undefined) {
				iconStyle[prop] = refStyle[prop];
			}
		}

		const closestListParent = parents.find(
			(p) => p.type === "bullet_list" || p.type === "ordered_list",
		);

		if (closestListParent?.type === "bullet_list") {
			const dotSize = Math.max(4, Math.round(fontSize * 0.3));
			const dotColor =
				(iconStyle.color as string | undefined) ?? theme.palette.text.primary;
			const rowLineHeight = fontSize * 1.5;
			return (
				<View
					key={node.key}
					style={[
						styles.list_item,
						{ flexDirection: "row", alignItems: "flex-start" },
					]}
				>
					<View
						style={{
							width: dotSize,
							height: dotSize,
							borderRadius: dotSize / 2,
							backgroundColor: dotColor,
							marginLeft: 4,
							marginRight: 10,
							marginTop: (rowLineHeight - dotSize) / 2,
						}}
					/>
					<View style={{ flex: 1, minWidth: 0 }}>
						{children as ReactNode[]}
					</View>
				</View>
			);
		}

		if (closestListParent?.type === "ordered_list") {
			const attrs = closestListParent.attributes as
				| { start?: number }
				| undefined;
			const listItemNumber =
				attrs?.start != null ? attrs.start + node.index : node.index + 1;
			return (
				<View
					key={node.key}
					style={[
						styles.list_item,
						{ flexDirection: "row", alignItems: "flex-start" },
					]}
				>
					<RNText
						style={[
							iconStyle,
							styles.ordered_list_icon,
							{ flexShrink: 0, marginTop: fontSize * 0.15 },
						]}
					>
						{listItemNumber}
						{node.markup}
					</RNText>
					<View style={{ flex: 1, minWidth: 0 }}>
						{children as ReactNode[]}
					</View>
				</View>
			);
		}

		return (
			<View key={node.key} style={styles.list_item}>
				{children as ReactNode[]}
			</View>
		);
	};

	const supRule: RenderFunction = (node, children, _parent, rawStyles) => {
		const styles = rawStyles as Styles;
		if (!isWeb) {
			return (
				<View
					key={node.key}
					style={{
						flexDirection: "row",
						alignItems: "flex-start",
						height: fontSize * 1.5,
					}}
				>
					<Text style={styles.sup}>{children as ReactNode[]}</Text>
				</View>
			);
		}
		return (
			<Text key={node.key} style={styles.sup}>
				{children as ReactNode[]}
			</Text>
		);
	};

	const subRule: RenderFunction = (node, children, _parent, rawStyles) => {
		const styles = rawStyles as Styles;
		if (!isWeb) {
			return (
				<View
					key={node.key}
					style={{
						flexDirection: "row",
						alignItems: "flex-end",
						height: fontSize * 1.5,
					}}
				>
					<Text style={styles.sub}>{children as ReactNode[]}</Text>
				</View>
			);
		}
		return (
			<Text key={node.key} style={styles.sub}>
				{children as ReactNode[]}
			</Text>
		);
	};

	const imageRule: RenderFunction = (node) => {
		const src = node.attributes?.src as string | undefined;
		if (!src) {
			return null;
		}
		return (
			<ZoomImage
				key={node.key}
				url={src}
				allImages={[{ url: src }]}
				{...(customStyles?.image as Record<string, unknown> | undefined)}
			/>
		);
	};

	const linkRule: RenderFunction = (node, children) => (
		<Text
			key={node.key}
			onPress={() => {
				const href = node.attributes?.href;
				if (typeof href === "string" && isSafeUrl(href)) {
					Linking.openURL(href);
				}
			}}
			style={{ textDecorationLine: "underline", color: theme.colors.outline }}
		>
			{children as ReactNode[]}
		</Text>
	);

	const rules: RenderRules = {
		paragraph: paragraphRule,
		bullet_list: bulletListRule,
		text: textRule,
		strong: strongRule,
		em: emRule,
		ins: insRule,
		hardbreak: hardbreakRule,
		softbreak: softbreakRule,
		list_item: listItemRule,
		sup: supRule,
		sub: subRule,
		image: imageRule,
	};

	if (useCustomLink) {
		rules.link = linkRule;
	}

	return rules;
};
